// Órgãos reutilizáveis dos documentos de RH — extraídos da antiga tela-lista
// PainelDocumentosRH.tsx (2026-08-25, aposentada no mesmo dia junto com o hub
// de abas): tipos da action rh_resumo, helpers de data/pendência, a FÁBRICA de
// colunas da tabela de documentos e os dois modais (novo/editar). Quem importa
// daqui hoje são a EquipeRH (placar + pendências) e a FichaProfissional (aba
// Documentos da pessoa).
//
// Quem pode o quê é decidido no backend (handlers/documentos.js):
//  - `rh_resumo` exige administrativo.rh.documentos.ver — 403 aqui é "você não
//    tem acesso", nunca tabela vazia (padrão PainelDocumentosEmpresa);
//  - `pode_gerenciar` vem do resumo e só esconde botão que responderia 403.
import { ReactElement, useState } from 'react';
import axios from 'axios';
import {
  Alert, Button, DatePicker, Flex, Input, Modal, Popconfirm, Select, Space,
  Spin, Tag, Tooltip, Typography, Upload, App as AntApp,
} from 'antd';
import {
  ContainerOutlined, DownloadOutlined, EditOutlined, InboxOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDataBr, dataAoMeioDia, hojeIso } from '../../utils/dataFmt';
import type { Documento, SubcategoriaDoc } from '../administrativo-documentos/types';
import {
  obterDocumento, criarDocumento, atualizarDocumento, arquivarDocumento,
  subirArquivo,
} from '../administrativo-documentos/api';

const { Text } = Typography;

// O `message` do AntApp.useApp() — derivado para não depender do caminho
// interno de tipos do antd.
export type MessageApi = ReturnType<typeof AntApp.useApp>['message'];

// ── Tipos da action rh_resumo (handlers/documentos.js › rhResumo) ──────────
export type SituacaoPendencia = 'faltando' | 'vencendo' | 'vencido';

export interface PendenciaRH {
  subcategoria: string;
  nome: string;
  situacao: SituacaoPendencia;
}

export interface PessoaRH {
  id: string;            // uuid de public.profissionais (desde a 1791400000000)
  nome: string;
  ativo: boolean;
  docs: number;          // total de documentos RH não arquivados da pessoa
  pendencias: PendenciaRH[];  // só vem para pessoa ATIVA
}

export interface VencendoRH {
  id: number;
  titulo: string;
  subcategoria_codigo: string | null;
  subcategoria_nome: string | null;
  data_validade: string;
  dias: number | string;  // date - date no PG; Number() antes de comparar
  profissional_id: string | null;
  pessoa: string | null;
}

export interface RhResumo {
  pessoas: PessoaRH[];
  exigidos: { codigo: string; nome: string }[];
  vencendo: VencendoRH[];   // validade até hoje + 60 dias, INCLUINDO já vencidos
  pode_gerenciar: boolean;
}

// A api.ts não exporta o `chamar` genérico, e `rh_resumo` não tem wrapper lá.
// Mesma chamada, mesmo desembrulho do body (padrão ModalDocumento.tsx).
export async function pedirRhResumo(): Promise<RhResumo> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/documentos`,
    { action: 'rh_resumo' }, { headers: { Authorization: token } });
  return r.data.body ? JSON.parse(r.data.body) : r.data;
}

// Dias até a validade, timezone-safe: nunca `new Date('YYYY-MM-DD')` direto
// (00:00 UTC vira véspera em BRT). Ambas as pontas ao meio-dia LOCAL.
export function diasAte(validade: string | null | undefined): number | null {
  const alvo = dataAoMeioDia(validade);
  const hoje = dataAoMeioDia(hojeIso());
  if (!alvo || !hoje) return null;
  return Math.round((alvo.getTime() - hoje.getTime()) / 86400000);
}

// "Fulano: sem ASO, EPI vencido" — a frase de cada pendência da régua.
export function fmtPendencia(p: PendenciaRH): string {
  if (p.situacao === 'faltando') return `sem ${p.nome}`;
  if (p.situacao === 'vencido') return `${p.nome} vencido`;
  return `${p.nome} vencendo`;
}

// ── Helpers de ação (eram funções do painel; agora recebem tudo por
//    parâmetro para a ficha reusar) ─────────────────────────────────────────

export async function baixarDocRH(row: Documento, message: MessageApi): Promise<void> {
  const hide = message.loading('Gerando link…', 0);
  try {
    const d = await obterDocumento(row.id, true);
    hide();
    if (d.download_url) window.open(d.download_url, '_blank', 'noopener,noreferrer');
    else message.error('Este documento não tem arquivo para baixar.');
  } catch (e: any) {
    hide();
    message.error('Erro ao baixar: ' + (e?.response?.data?.error || e.message));
  }
}

export async function arquivarDocRH({ row, para, motivo, message, aoMudar }: {
  row: Documento;
  para: boolean;          // true = arquivar, false = desarquivar
  motivo?: string;        // só usado ao arquivar
  message: MessageApi;
  aoMudar: () => void;    // recarregar a tela depois do sucesso
}): Promise<void> {
  try {
    await arquivarDocumento({
      id: row.id, arquivado: para,
      motivo: para ? (motivo?.trim() || undefined) : undefined,
    });
    message.success(para ? 'Documento arquivado.' : 'Documento desarquivado.');
    aoMudar();
  } catch (e: any) {
    message.error('Não foi possível: ' + (e?.response?.data?.error || e.message));
  }
}

// ── Fábrica de colunas da tabela de documentos ─────────────────────────────
// A coluna "Pessoa" só entra com `mostrarPessoa` (a ficha de UMA pessoa não
// precisa repetir o nome em toda linha). As larguras são as do painel; quem
// monta a tabela soma o piso (`scroll={{ x }}`) conforme as colunas visíveis.
export function colunasDocsRH({
  mostrarPessoa, mapaPessoas, podeGerenciar,
  onBaixar, onEditar, onArquivar, onDesarquivar,
  motivoArquivar, setMotivoArquivar,
}: {
  mostrarPessoa: boolean;
  mapaPessoas: Map<string, PessoaRH>;
  podeGerenciar: boolean;
  onBaixar: (row: Documento) => void;
  onEditar: (row: Documento) => void;
  onArquivar: (row: Documento) => void;      // o motivo é lido pelo chamador (estado motivoArquivar)
  onDesarquivar: (row: Documento) => void;
  motivoArquivar: string;
  setMotivoArquivar: (v: string) => void;
}): ColumnsType<Documento> {
  const cols: ColumnsType<Documento> = [];

  if (mostrarPessoa) {
    cols.push({
      title: 'Pessoa', key: 'pessoa', width: 170,
      render: (_, row) => {
        const pid = row.profissional_id != null ? String(row.profissional_id) : '';
        const p = pid ? mapaPessoas.get(pid) : undefined;
        if (!p) return <Text type="secondary" style={{ fontSize: 12 }}>sem pessoa</Text>;
        return (
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.nome}>
            <Text style={{ fontSize: 12 }}>{p.nome}</Text>
            {!p.ativo && <Tag style={{ fontSize: 9, marginLeft: 4 }}>desligado</Tag>}
          </div>
        );
      },
    });
  }

  cols.push(
    {
      title: 'Tipo', dataIndex: 'subcategoria_nome', key: 'tipo', width: 150,
      render: (v: string | null) => v
        ? (
          <Tooltip title={v}>
            <Tag color="blue" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {v}
            </Tag>
          </Tooltip>
        )
        : <Text type="secondary" style={{ fontSize: 11 }}>—</Text>,
    },
    {
      // SEM width: coluna elástica — fica com a sobra da tela.
      title: 'Título', dataIndex: 'titulo', key: 'titulo',
      render: (v: string, row) => (
        <div>
          {/* Teto de 2 linhas: espremido, a linha não vira parágrafo. */}
          <Text strong title={v} style={{
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>{v}</Text>
          {row.arquivo_nome_original && (
            <div title={row.arquivo_nome_original} style={{
              fontSize: 10, color: '#888',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{row.arquivo_nome_original}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Emissão', dataIndex: 'data_emissao', key: 'emissao', width: 92,
      responsive: ['lg'],
      render: (v: string | null) => <span style={{ whiteSpace: 'nowrap', fontSize: 11 }}>{fmtDataBr(v)}</span>,
    },
    {
      title: 'Validade', dataIndex: 'data_validade', key: 'validade', width: 175,
      render: (v: string | null, row) => {
        // Arquivado ganha o lugar do status: "Vencido" num papel substituído
        // é alarme falso.
        if (row.arquivado) {
          return (
            <Tooltip title={row.arquivado_motivo || 'Arquivado — não conta no placar nem na régua'}>
              <Tag>Arquivado</Tag>
            </Tooltip>
          );
        }
        if (!v) return <Text type="secondary" style={{ fontSize: 11 }}>Permanente</Text>;
        const n = diasAte(v);
        const tag = n !== null && n < 0
          ? <Tag color="red">Vencido</Tag>
          : n !== null && n <= 60
            ? <Tag color="orange">{n === 0 ? 'Vence hoje' : `Vence em ${n} d`}</Tag>
            : <Tag color="green">Vigente</Tag>;
        return (
          <Space size={4} wrap>
            <span style={{ whiteSpace: 'nowrap', fontSize: 11 }}>{fmtDataBr(v)}</span>
            {tag}
          </Space>
        );
      },
    },
    {
      title: 'Ações', key: 'acoes', width: podeGerenciar ? 122 : 56, align: 'center', fixed: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Button size="small" icon={<DownloadOutlined />} title="Baixar o arquivo"
            onClick={(e) => { e.stopPropagation(); onBaixar(row); }} />
          {podeGerenciar && (
            <Button size="small" type="text" icon={<EditOutlined />} title="Editar"
              onClick={(e) => { e.stopPropagation(); onEditar(row); }} />
          )}
          {podeGerenciar && (row.arquivado
            ? (
              <Button size="small" type="text" icon={<InboxOutlined />}
                title="Desarquivar: volta a contar no placar e na régua"
                onClick={(e) => { e.stopPropagation(); onDesarquivar(row); }} />
            )
            : (
              <Popconfirm
                title={`Arquivar "${row.titulo}"?`}
                description={
                  <div style={{ maxWidth: 300 }}>
                    <div style={{ fontSize: 12, marginBottom: 6 }}>
                      Sai da lista e do placar sem apagar nada — volta pelo filtro
                      "Arquivados". Use para documento substituído (ASO novo, EPI trocado).
                    </div>
                    <Input size="small" placeholder="Motivo (opcional)"
                      value={motivoArquivar}
                      onChange={(e) => setMotivoArquivar(e.target.value)}
                      onClick={(e) => e.stopPropagation()} />
                  </div>
                }
                okText="Arquivar" cancelText="Cancelar"
                onOpenChange={(open) => { if (open) setMotivoArquivar(''); }}
                onConfirm={() => onArquivar(row)}
              >
                <Button size="small" type="text" icon={<ContainerOutlined />}
                  title="Arquivar: sai da lista e do placar sem apagar"
                  onClick={(e) => e.stopPropagation()} />
              </Popconfirm>
            ))}
        </Space>
      ),
    },
  );

  return cols;
}

// ── Novo documento: pessoa + tipo obrigatórios, upload de UM arquivo ───────
// Fluxo: presigned upload (PUT direto no S3, via subirArquivo da api) → criar.
// Com `pessoaFixa` (a ficha de uma pessoa) o Select de pessoa some: o vínculo
// já vem travado e o título sugerido usa o nome dela.
export function ModalNovoDocRH({ pessoas = [], subcategorias, pessoaFixa, onClose, onSaved }: {
  pessoas?: PessoaRH[];
  subcategorias: SubcategoriaDoc[];
  pessoaFixa?: { id: string; nome: string };
  onClose: () => void;
  onSaved: () => void;
}): ReactElement {
  const { message } = AntApp.useApp();
  const [f, setF] = useState({
    profissional_id: pessoaFixa ? String(pessoaFixa.id) : '',
    subcategoria_codigo: '', titulo: '',
    data_emissao: '', data_validade: '',
  });
  // Título sugerido "<Tipo> — <Pessoa>" enquanto a pessoa não escreve o dela;
  // apagar o campo volta a sugerir.
  const [tituloEditado, setTituloEditado] = useState(false);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [progresso, setProgresso] = useState('');

  const nomeDe = (pessoaId: string): string => {
    if (pessoaFixa && String(pessoaFixa.id) === pessoaId) return pessoaFixa.nome;
    return pessoas.find((p) => String(p.id) === pessoaId)?.nome || '';
  };

  const sugerirTitulo = (subCod: string, pessoaId: string): string => {
    const tipo = subcategorias.find((s) => s.codigo === subCod)?.nome || '';
    const nome = nomeDe(pessoaId);
    if (tipo && nome) return `${tipo} — ${nome}`;
    return tipo || nome;
  };

  const escolher = (k: 'profissional_id' | 'subcategoria_codigo', v: string) => {
    setF((prev) => {
      const prox = { ...prev, [k]: v };
      if (!tituloEditado) prox.titulo = sugerirTitulo(prox.subcategoria_codigo, prox.profissional_id);
      return prox;
    });
  };

  async function salvar() {
    if (!f.profissional_id) { message.warning('Escolha de quem é o documento'); return; }
    if (!f.subcategoria_codigo) { message.warning('Escolha o tipo do documento'); return; }
    if (!f.titulo.trim()) { message.warning('Dê um título ao documento'); return; }
    if (!arquivo) { message.warning('Selecione o arquivo'); return; }
    setSalvando(true);
    try {
      setProgresso('Enviando o arquivo...');
      const s3_key = await subirArquivo(arquivo, 'rh');
      setProgresso('Cadastrando o documento...');
      await criarDocumento({
        categoria_codigo: 'rh',
        subcategoria_codigo: f.subcategoria_codigo,
        titulo: f.titulo.trim(),
        profissional_id: f.profissional_id,
        data_emissao: f.data_emissao || null,
        data_validade: f.data_validade || null,
        s3_key,
        filename_original: arquivo.name,
      });
      message.success('Documento salvo.');
      onSaved();
    } catch (e: any) {
      message.error('Não foi possível salvar: ' + (e?.response?.data?.error || e.message));
    } finally { setSalvando(false); setProgresso(''); }
  }

  return (
    <Modal open onCancel={() => !salvando && onClose()} onOk={salvar} confirmLoading={salvando}
      title="Novo documento de RH" okText="Salvar" cancelText="Cancelar"
      // Regra da casa: modal usa a tela que houver, sem rolagem forçada.
      width="94vw" style={{ maxWidth: 680, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' } }}
      destroyOnClose>
      {salvando && progresso && (
        <Alert message={<Spin size="small" />} description={progresso}
          type="info" style={{ marginBottom: 12 }} />
      )}
      <Flex vertical gap={10} style={{ marginTop: 8 }}>
        <Flex gap={8} wrap>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>De quem é</Text>
            {pessoaFixa
              ? (
                // Pessoa travada: a ficha já diz de quem é — trocar aqui seria
                // cadastrar o documento na pessoa errada sem perceber.
                <div style={{ paddingTop: 5 }}>
                  <Text strong>{pessoaFixa.nome}</Text>
                </div>
              )
              : (
                <Select style={{ width: '100%' }} value={f.profissional_id || undefined}
                  showSearch optionFilterProp="label" placeholder="Escolha a pessoa"
                  onChange={(v) => escolher('profissional_id', v || '')}
                  options={pessoas.map((p) => ({
                    label: p.ativo ? p.nome : `${p.nome} (desligado)`,
                    value: String(p.id),
                  }))} />
              )}
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Tipo do documento</Text>
            <Select style={{ width: '100%' }} value={f.subcategoria_codigo || undefined}
              showSearch optionFilterProp="label" placeholder="Contrato, ASO, EPI, CNH..."
              onChange={(v) => escolher('subcategoria_codigo', v || '')}
              options={subcategorias.map((s) => ({ label: s.nome, value: s.codigo }))} />
          </div>
        </Flex>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Título</Text>
          <Input value={f.titulo}
            onChange={(e) => {
              setF((prev) => ({ ...prev, titulo: e.target.value }));
              setTituloEditado(e.target.value.trim() !== '');
            }}
            placeholder="Sugerido a partir do tipo e da pessoa — pode trocar" />
        </div>
        <Flex gap={8} wrap>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Data de emissão (opcional)</Text>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
              value={f.data_emissao ? dayjs(f.data_emissao) : null}
              onChange={(d) => setF((prev) => ({ ...prev, data_emissao: d ? d.format('YYYY-MM-DD') : '' }))} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Validade (vazio = permanente)</Text>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
              value={f.data_validade ? dayjs(f.data_validade) : null}
              onChange={(d) => setF((prev) => ({ ...prev, data_validade: d ? d.format('YYYY-MM-DD') : '' }))} />
          </div>
        </Flex>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Arquivo</Text>
          <Upload.Dragger
            maxCount={1} multiple={false}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.png,.jpg,.jpeg"
            beforeUpload={(file) => { setArquivo(file as File); return false; }}
            onRemove={() => setArquivo(null)}
            fileList={arquivo ? [{ uid: '1', name: arquivo.name, status: 'done' } as any] : []}>
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Solte o arquivo aqui ou clique para escolher</p>
            <p className="ant-upload-hint">Um arquivo por documento. PDF, Word, Excel, imagem ou texto.</p>
          </Upload.Dragger>
        </div>
      </Flex>
    </Modal>
  );
}

// ── Edição: só metadata (título, tipo, pessoa, datas) — arquivo não troca ──
export function ModalEditarDocRH({ doc, pessoas, subcategorias, onClose, onSaved }: {
  doc: Documento;
  pessoas: PessoaRH[];
  subcategorias: SubcategoriaDoc[];
  onClose: () => void;
  onSaved: () => void;
}): ReactElement {
  const { message } = AntApp.useApp();
  const [f, setF] = useState({
    titulo: doc.titulo || '',
    subcategoria_codigo: doc.subcategoria_codigo || '',
    // O vínculo é uuid (string) desde a 1791400000000; o String() fica como
    // cinto para resposta antiga em cache.
    profissional_id: doc.profissional_id != null ? String(doc.profissional_id) : '',
    data_emissao: doc.data_emissao?.slice(0, 10) || '',
    data_validade: doc.data_validade?.slice(0, 10) || '',
  });
  const [salvando, setSalvando] = useState(false);
  const set = (k: string, v: string) => setF((prev) => ({ ...prev, [k]: v }));

  async function salvar() {
    if (!f.titulo.trim()) { message.warning('O título não pode ficar vazio'); return; }
    setSalvando(true);
    try {
      await atualizarDocumento({
        id: doc.id,
        titulo: f.titulo.trim(),
        subcategoria_codigo: f.subcategoria_codigo || null,
        profissional_id: f.profissional_id || null,
        data_emissao: f.data_emissao || null,
        data_validade: f.data_validade || null,
      });
      message.success('Documento atualizado.');
      onSaved();
    } catch (e: any) {
      message.error('Não foi possível salvar: ' + (e?.response?.data?.error || e.message));
    } finally { setSalvando(false); }
  }

  return (
    <Modal open onCancel={() => !salvando && onClose()} onOk={salvar} confirmLoading={salvando}
      title="Editar documento de RH" okText="Salvar" cancelText="Cancelar"
      width="94vw" style={{ maxWidth: 680, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' } }}
      destroyOnClose>
      <Flex vertical gap={10} style={{ marginTop: 8 }}>
        <Flex gap={8} wrap>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>De quem é</Text>
            <Select style={{ width: '100%' }} value={f.profissional_id || undefined}
              showSearch optionFilterProp="label" allowClear
              placeholder="Sem pessoa o documento fica fora da régua"
              onChange={(v) => set('profissional_id', v || '')}
              options={pessoas.map((p) => ({
                label: p.ativo ? p.nome : `${p.nome} (desligado)`,
                value: String(p.id),
              }))} />
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Tipo do documento</Text>
            <Select style={{ width: '100%' }} value={f.subcategoria_codigo || undefined}
              showSearch optionFilterProp="label" allowClear
              placeholder="Contrato, ASO, EPI, CNH..."
              onChange={(v) => set('subcategoria_codigo', v || '')}
              options={subcategorias.map((s) => ({ label: s.nome, value: s.codigo }))} />
          </div>
        </Flex>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Título</Text>
          <Input value={f.titulo} onChange={(e) => set('titulo', e.target.value)} />
        </div>
        <Flex gap={8} wrap>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Data de emissão</Text>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" allowClear
              value={f.data_emissao ? dayjs(f.data_emissao) : null}
              onChange={(d) => set('data_emissao', d ? d.format('YYYY-MM-DD') : '')} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Validade (vazio = permanente)</Text>
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" allowClear
              value={f.data_validade ? dayjs(f.data_validade) : null}
              onChange={(d) => set('data_validade', d ? d.format('YYYY-MM-DD') : '')} />
          </div>
        </Flex>
        {doc.arquivo_nome_original && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            Arquivo: {doc.arquivo_nome_original}. A troca de arquivo é feita pelo
            acervo de Documentos da empresa; aqui edita-se só a ficha.
          </Text>
        )}
      </Flex>
    </Modal>
  );
}
